import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Loader2, Plus, Save, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const today = () => new Date().toISOString().slice(0, 10);
const lei = (v) => (v ?? 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
const r2 = (v) => Math.round(v * 100) / 100;
export const PAYMENT_TYPES = [['chitanta', 'Chitanță'], ['op', 'OP / transfer'], ['numerar', 'Numerar'], ['card', 'Card'], ['altele', 'Altele']];
export const tipLabel = (t) => (t === 'storno' ? 'Storno (compensare)' : PAYMENT_TYPES.find(([k]) => k === t)?.[1] || 'Plată');

/** Împarte suma pe facturile bifate, în ordinea scadenței: fiecare primește restul ei, ultima primește tot ce rămâne. */
export const autoSplit = (suma, selected) => {
  let left = r2(suma);
  const out = {};
  selected.forEach((inv, i) => {
    const need = Math.max(0, r2(inv.rest ?? inv.total));
    const give = i === selected.length - 1 ? left : Math.min(need, left);
    out[inv.id] = r2(Math.max(0, give));
    left = r2(left - out[inv.id]);
  });
  return out;
};

/**
 * Formular plată la furnizor cu alocare pe mai multe facturi.
 * props: cod, preselectId, initial (plată existentă pentru editare), onSaved, onCancel, compact
 */
export default function PlataForm({ cod, preselectId, initial = null, onSaved, onCancel, compact = false }) {
  const [inv, setInv] = useState(null);
  const [stornos, setStornos] = useState([]);
  const [sursa, setSursa] = useState(initial?.storno_id || '');
  const [suma, setSuma] = useState(initial ? String(initial.suma) : '');
  const [data, setData] = useState(initial?.data || today());
  const [tip, setTip] = useState(initial?.tip_plata || 'chitanta');
  const [obs, setObs] = useState(initial?.observatii || '');
  const [file, setFile] = useState(null);
  const [sel, setSel] = useState(() => Object.fromEntries((initial?.alocari || []).map((a) => [a.factura_id, String(a.suma)])));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/furnizori/facturi`, { ...auth(), params: { cod } })
      .then((r) => {
        const rows = r.data.rows.filter((x) => x.total > 0)
          .sort((a, b) => (a.scadent || a.data || '').localeCompare(b.scadent || b.data || ''));
        setInv(rows);
        setStornos(r.data.rows.filter((x) => x.total < 0 && (x.credit_ramas > 0.005 || x.id === initial?.storno_id))
          .map((x) => ({ ...x, credit: r2((x.credit_ramas || 0) + (x.id === initial?.storno_id ? initial.suma : 0)) })));
        if (!initial && preselectId) {
          const p = rows.find((x) => x.id === preselectId);
          if (p) { const def = p.rest > 0 ? p.rest : p.total; setSuma(String(def)); setSel({ [p.id]: String(def) }); }
        }
      }).catch(() => toast.error('Nu s-au putut încărca facturile furnizorului'));
  }, [cod, preselectId, initial]);

  const selected = useMemo(() => (inv || []).filter((x) => sel[x.id] != null), [inv, sel]);
  const alocat = r2(selected.reduce((s, x) => s + num(sel[x.id]), 0));
  const total = num(suma);
  const nealocat = r2(total - alocat);
  const open = (inv || []).filter((x) => x.stare_plata !== 'achitata' || sel[x.id] != null || x.id === preselectId);
  const done = (inv || []).filter((x) => !open.includes(x));
  const [showDone, setShowDone] = useState(false);

  const toggle = (x) => {
    const next = { ...sel };
    if (next[x.id] != null) delete next[x.id]; else next[x.id] = '';
    const list = (inv || []).filter((i) => next[i.id] != null);
    const split = autoSplit(total, list);
    list.forEach((i) => { next[i.id] = String(split[i.id] ?? 0); });
    setSel(next);
  };
  const redistribute = () => { const split = autoSplit(total, selected); setSel(Object.fromEntries(selected.map((i) => [i.id, String(split[i.id] ?? 0)]))); };
  useEffect(() => { if (selected.length === 1) setSel((s) => ({ [selected[0].id]: String(total) })); }, [total]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (total <= 0) return toast.error('Introdu suma plății');
    const st = stornos.find((x) => x.id === sursa);
    if (st && total > st.credit + 0.005) return toast.error(`Storno ${st.nr_intrare} mai are doar ${lei(st.credit)} lei credit`);
    if (!selected.length) return toast.error('Bifează cel puțin o factură');
    if (Math.abs(nealocat) > 0.005) return toast.error(nealocat > 0 ? `Mai ai ${lei(nealocat)} lei nealocați — pune-i pe o factură` : `Ai alocat cu ${lei(-nealocat)} lei mai mult decât suma plății`);
    const alocari = selected.map((x) => ({ factura_id: x.id, suma: r2(num(sel[x.id])) })).filter((a) => a.suma > 0);
    setBusy(true);
    try {
      if (initial) {
        await axios.put(`${API_URL}/furnizori/plati/${initial.id}`, { cod, suma: total, data, tip_plata: tip, observatii: obs || null, alocari }, auth());
      } else {
        const fd = new FormData();
        fd.append('cod', cod); fd.append('suma', String(total)); fd.append('data', data); fd.append('tip_plata', tip); fd.append('alocari', JSON.stringify(alocari));
        if (sursa) fd.append('storno_id', sursa);
        if (obs) fd.append('observatii', obs); if (file) fd.append('file', file);
        await axios.post(`${API_URL}/furnizori/plati`, fd, auth());
      }
      toast.success(initial ? 'Plată actualizată' : 'Plată înregistrată');
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
    finally { setBusy(false); }
  };

  const Row = ({ x }) => {
    const on = sel[x.id] != null;
    const dif = on ? r2((x.platit || 0) - (initial?.alocari?.find((a) => a.factura_id === x.id)?.suma || 0) + num(sel[x.id]) - x.total) : null;
    return (
      <label className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs cursor-pointer ${on ? 'bg-white border-blue-300' : 'bg-slate-50/50 border-transparent hover:bg-white'}`} data-testid={`plata-inv-${x.id}`}>
        <input type="checkbox" checked={on} onChange={() => toggle(x)} className="accent-blue-600" data-testid={`plata-inv-check-${x.id}`} />
        <span className="font-mono w-28 truncate" title={x.nr_intrare}>{x.nr_intrare}</span>
        <span className="text-slate-500 w-20 hidden sm:inline">{fmtD(x.data)}</span>
        <span className="text-slate-500 w-20 hidden sm:inline" title="scadență">sc. {fmtD(x.scadent) || '—'}</span>
        <span className="ml-auto text-slate-700 whitespace-nowrap">{lei(x.total)}{x.stare_plata !== 'fara' && x.stare_plata !== 'storno' && <span className={`ml-1 ${x.rest > 0.005 ? 'text-red-600' : 'text-emerald-600'}`}>· rest {lei(x.rest)}</span>}</span>
        {on && (
          <span className="flex items-center gap-1">
            <Input className="h-7 w-24 text-right bg-white" inputMode="decimal" value={sel[x.id]} onClick={(e) => e.preventDefault()} onChange={(e) => setSel({ ...sel, [x.id]: e.target.value })} data-testid={`plata-inv-suma-${x.id}`} />
            {dif != null && <span className={`w-16 text-right font-medium ${Math.abs(dif) < 0.005 ? 'text-emerald-600' : dif > 0 ? 'text-blue-600' : 'text-red-600'}`} title="diferență după această plată">{Math.abs(dif) < 0.005 ? '0' : dif > 0 ? `+${lei(dif)}` : `−${lei(-dif)}`}</span>}
          </span>
        )}
      </label>
    );
  };

  return (
    <form onSubmit={submit} className={`rounded-lg border bg-slate-50 ${compact ? 'p-2 space-y-2' : 'p-3 space-y-3'}`} data-testid="plata-form">
      <h4 className="text-sm font-semibold flex items-center gap-1.5">{initial ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {initial ? 'Modifică plata' : 'Înregistrează o plată'}</h4>
      {stornos.length > 0 && (
        <div>
          <Label className="text-xs">Sursa</Label>
          <select className="h-8 w-full md:w-auto rounded-md border bg-white px-2 text-sm" value={sursa} disabled={!!initial} onChange={(e) => { const v = e.target.value; setSursa(v); const st = stornos.find((x) => x.id === v); if (st) { setSuma(String(st.credit)); setTip('storno'); } else if (tip === 'storno') setTip('chitanta'); }} data-testid="plata-sursa">
            <option value="">Plată (bani: chitanță / OP / numerar)</option>
            {stornos.map((x) => <option key={x.id} value={x.id}>Storno {x.nr_intrare} ({fmtD(x.data)}) — credit {lei(x.credit)} lei</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div><Label className="text-xs">{sursa ? 'Sumă compensată (lei)' : 'Sumă plătită (lei)'}</Label><Input className="h-8 bg-white" inputMode="decimal" value={suma} onChange={(e) => setSuma(e.target.value)} data-testid="plata-suma" /></div>
        <div><Label className="text-xs">Data</Label><Input className="h-8 bg-white" type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="plata-data" /></div>
        <div><Label className="text-xs">Tip</Label>
          <select className="h-8 w-full rounded-md border bg-white px-2 text-sm" value={tip} disabled={!!sursa} onChange={(e) => setTip(e.target.value)} data-testid="plata-tip">{sursa ? <option value="storno">Storno (compensare)</option> : PAYMENT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div className="col-span-2"><Label className="text-xs">Observații (nr. chitanță / OP…)</Label><Input className="h-8 bg-white" value={obs} onChange={(e) => setObs(e.target.value)} data-testid="plata-observatii" /></div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Facturile pe care le stinge plata <span className="text-slate-400">(bifează una sau mai multe)</span></Label>
          {selected.length > 1 && <button type="button" onClick={redistribute} className="text-[11px] text-blue-700 flex items-center gap-1 hover:underline" data-testid="plata-redistribute"><Wand2 className="w-3 h-3" /> Împarte automat după scadență</button>}
        </div>
        {!inv ? <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Se încarcă facturile…</div> : (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid="plata-inv-list">
            {open.length === 0 && <div className="text-xs text-slate-400 px-1">Toate facturile furnizorului sunt achitate.</div>}
            {open.map((x) => <Row key={x.id} x={x} />)}
            {done.length > 0 && (
              <button type="button" className="text-[11px] text-slate-500 underline px-1" onClick={() => setShowDone((v) => !v)} data-testid="plata-show-done">{showDone ? 'ascunde' : 'arată'} {done.length} facturi deja achitate</button>
            )}
            {showDone && done.map((x) => <Row key={x.id} x={x} />)}
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1" data-testid="plata-sumar">
          <span>Plată: <b>{lei(total)}</b></span>
          <span>Alocat: <b>{lei(alocat)}</b> pe {selected.length} {selected.length === 1 ? 'factură' : 'facturi'}</span>
          <span className={Math.abs(nealocat) < 0.005 ? 'text-emerald-700' : 'text-red-600 font-semibold'} data-testid="plata-nealocat">{nealocat > 0.005 ? `Nealocat: ${lei(nealocat)}` : nealocat < -0.005 ? `Alocat în plus: ${lei(-nealocat)}` : 'Alocare completă'}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!initial && !sursa && (
          <>
            <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="plata-file-input" />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="plata-file-btn"><Camera className="w-4 h-4 mr-1" /> {file ? file.name : 'Dovadă (poză / PDF, opțional)'}</Button>
          </>
        )}
        {onCancel && <Button type="button" size="sm" variant="ghost" onClick={onCancel} data-testid="plata-cancel">Renunță</Button>}
        <Button type="submit" size="sm" className="ml-auto" disabled={busy || !inv} data-testid="plata-submit">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : initial ? <Save className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />} {initial ? 'Salvează modificările' : 'Salvează plata'}</Button>
      </div>
    </form>
  );
}
